import { useEffect, useMemo, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, deleteDoc, doc, onSnapshot, updateDoc } from "firebase/firestore";
import NotificationBar from "./NotificationBar";
import { writeAuditLog } from "./utils/auditLog";
import "./MyHistory.css";
import { calculateRecordFee, canonicalTeamName } from "./utils/feeRules";

// ---------- Helpers ----------
const fmt = (n) => Number(n || 0).toLocaleString("th-TH");

const getRecDate = (rec) => {
  if (rec?.createdAt?.toDate) return rec.createdAt.toDate();
  if (rec?.date) {
    const d = new Date(rec.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (rec?.pressDate) {
    const d = new Date(rec.pressDate);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

// กติกาค่ากดใช้จาก utils/feeRules.js ร่วมกันทุกหน้า

const calcEffective = (r) => calculateRecordFee(r);
const isOwnerRecord = (r) => canonicalTeamName(r?.teamName) === "ยย";
const isSettledRecord = (r) => isOwnerRecord(r) || !!r?.isPaid;
// ล็อกการลบเฉพาะรายการที่ "โอนแล้วจริง" — ค่ากดของยยไม่มีการโอน จึงยังลบ/แก้ได้
// (ค่ากดของยยถูกบันทึก isPaid=true ตั้งแต่ตอนกรอก เพราะไม่ต้องโอน → ไม่นับเป็นล็อก)
const isLockedRecord = (r) => !isOwnerRecord(r) && (!!r?.isPaid || !!r?.settlementId);

// ✅ คืน { tax, net } โดยยึดยอดที่เคยบันทึกใน database ก่อน เพื่อไม่เปลี่ยนประวัติย้อนหลัง
const taxParts = (rec) => {
  const eff = calcEffective(rec);
  return { tax: eff.tax, net: eff.net };
};

const monthNameTH = (m) => {
  const names = [
    "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
    "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
  ];
  return names[m - 1] || `เดือน ${m}`;
};

const formatDateTime = (d) => {
  if (!d) return "-";
  return d.toLocaleString("th-TH", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function MyHistory({ readOnly = false }) {
  const [now] = useState(() => new Date());
  const thisMonth = now.getMonth() + 1;

  const [records, setRecords] = useState([]);

  // ✅ รายปี
  const [yearFilter, setYearFilter] = useState(String(now.getFullYear()));

  // ✅ modal เดือน
  const [selectedMonth, setSelectedMonth] = useState(null); // 1..12

  // ✅ modal งาน
  const [selectedEventInMonth, setSelectedEventInMonth] = useState(null); // eventName
  const [selectedZone, setSelectedZone] = useState(null); // string | null
  const [showRawEventRecords, setShowRawEventRecords] = useState(false);

  const [notification, setNotification] = useState("");
  const [deletingId, setDeletingId] = useState("");
  // แก้ค่ากดของยย (เจ้าของร้าน) ในหน้าประวัติ
  const [ownerEditId, setOwnerEditId] = useState("");
  const [ownerEditForm, setOwnerEditForm] = useState({ fee: "", qty: "" });
  const [savingOwnerEdit, setSavingOwnerEdit] = useState(false);

  const openOwnerEdit = (rec) => {
    setOwnerEditId(rec.id);
    setOwnerEditForm({
      fee: String(calcEffective(rec).feePer || rec.feePerTicket || 0),
      qty: String(Number(rec.quantity || 1)),
    });
  };

  const saveOwnerEdit = async (rec) => {
    if (readOnly || !isOwnerRecord(rec) || savingOwnerEdit) return;
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const feePerTicket = Math.round(Number(ownerEditForm.fee || 0) * 100) / 100;
    const quantity = Number(ownerEditForm.qty);
    if (!(feePerTicket >= 0) || !Number.isInteger(quantity) || quantity < 1) {
      setNotification("ค่ากดต้องไม่ติดลบ และจำนวนใบต้องเป็นจำนวนเต็มตั้งแต่ 1 ใบ");
      setTimeout(() => setNotification(""), 3500);
      return;
    }
    const totalFee = Math.round(feePerTicket * quantity * 100) / 100;
    const oldFee = calcEffective(rec);
    const ok = window.confirm(
      `ยืนยันแก้ค่ากด ${rec.eventName || ""} ${rec.zone || rec.priceLabel || ""}\n\n` +
      `เดิม: ${fmt(oldFee.feePer || rec.feePerTicket)} บาท × ${fmt(rec.quantity)} ใบ = ${fmt(oldFee.baseFee)} บาท\n` +
      `ใหม่: ${fmt(feePerTicket)} บาท × ${fmt(quantity)} ใบ = ${fmt(totalFee)} บาท\n\n` +
      `ข้อมูลเดิมจะถูกเก็บไว้ใน Audit Log`
    );
    if (!ok) return;
    // ค่ากดของยย: ไม่หักค่าคอม ไม่หัก ณ ที่จ่าย (ตามกติกาเดิม)
    const patch = {
      feePerTicket,
      quantity,
      totalFee,
      commissionRate: 0,
      totalDeducted: 0,
      teamBeforeTax: totalFee,
      applyWht: false,
      withholding3pct: 0,
      netIncome: totalFee,
      calculationSnapshot: true,
      updatedAt: new Date(),
      updatedByUid: currentUser.uid,
      updatedByEmail: currentUser.email || "",
    };
    setSavingOwnerEdit(true);
    try {
      await writeAuditLog({ user: currentUser, action: "owner-fee.update", entityType: "press-fee", entityId: rec.id, title: rec.eventName || "ค่ากดของยย", before: rec, after: { ...rec, ...patch } });
      await updateDoc(doc(db, `user-press-fee/${currentUser.uid}/records`, rec.id), patch);
      setOwnerEditId("");
      setNotification("แก้ไขค่ากดเรียบร้อย");
    } catch (error) {
      console.error("owner edit failed", error);
      setNotification("แก้ไขไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSavingOwnerEdit(false);
      setTimeout(() => setNotification(""), 3500);
    }
  };

  // ---------- Load records ----------
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!u) return;
      const ref = collection(db, `user-press-fee/${u.uid}/records`);
      const unsubSnap = onSnapshot(ref, async (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRecords(data);

        // แจ้งเตือนโอนแล้ว 1 ครั้งต่อรายการ
        const newlyPaid = data.find((r) => !isOwnerRecord(r) && r.isPaid && !r.notified);
        if (newlyPaid && !readOnly) {
          setNotification("ยยโอนค่ากดให้แล้วน้าาา ขอบคุณมากค้าบบบ💗");
          try {
            await updateDoc(doc(db, `user-press-fee/${u.uid}/records`, newlyPaid.id), {
              notified: true,
            });
          } catch (e) {
            console.error("update notified failed", e);
          }
          setTimeout(() => setNotification(""), 5000);
        }
      });

      return () => unsubSnap();
    });

    return () => unsubAuth();
  }, [readOnly]);

  // ---------- Year options from real data ----------
  const yearOptions = useMemo(() => {
    const ys = new Set();
    records.forEach((r) => {
      const d = getRecDate(r);
      if (d) ys.add(d.getFullYear());
    });

    // ✅ บังคับให้มีตัวเลือกปี 2026–2027 ไว้ล่วงหน้า (แม้ยังไม่มีข้อมูลของปีนั้น)
    ys.add(2026);
    ys.add(2027);

    if (ys.size === 0) ys.add(now.getFullYear());

    return Array.from(ys).sort((a, b) => b - a).map(String);
  }, [records, now]);

  useEffect(() => {
    if (!yearOptions.includes(yearFilter)) {
      setYearFilter(yearOptions[0] || String(now.getFullYear()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearOptions]);

  // ---------- Filtered records by year ----------
  const yearRecords = useMemo(() => {
    const y = Number(yearFilter);
    return records
      .map((r) => ({ ...r, __date: getRecDate(r) }))
      .filter((r) => r.__date && r.__date.getFullYear() === y);
  }, [records, yearFilter]);

  // ---------- Group by month (1..12) ----------
  const months = useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      qty: 0,
      tax: 0,
      net: 0,
      unpaidCount: 0,
      paidCount: 0,
      records: [],
    }));

    yearRecords.forEach((rec) => {
      const d = rec.__date;
      const m = d.getMonth() + 1;
      const idx = m - 1;

      const p = taxParts(rec);
      base[idx].qty += Number(rec.quantity || 0);
      base[idx].tax += p.tax;
      base[idx].net += p.net;
      base[idx].records.push(rec);

      if (isSettledRecord(rec)) base[idx].paidCount += 1;
      else base[idx].unpaidCount += 1;
    });

    // เรียงรายการในแต่ละเดือนใหม่ล่าสุดก่อน
    base.forEach((b) => {
      b.records.sort((a, c) => (c.__date?.getTime?.() || 0) - (a.__date?.getTime?.() || 0));
    });

    return base;
  }, [yearRecords]);

  // ---------- Year totals ----------
  const yearTotal = useMemo(() => {
    return months.reduce(
      (a, m) => ({
        qty: a.qty + m.qty,
        tax: a.tax + m.tax,
        net: a.net + m.net,
        paidCount: a.paidCount + m.paidCount,
        unpaidCount: a.unpaidCount + m.unpaidCount,
        recordCount: a.recordCount + m.records.length,
      }),
      { qty: 0, tax: 0, net: 0, paidCount: 0, unpaidCount: 0, recordCount: 0 }
    );
  }, [months]);

  // ✅ โชว์ “ยอดเดือนนี้” บนหน้าเลย (ไม่ต้องกด)
  const thisMonthObj = useMemo(() => {
    return (
      months.find((m) => m.month === thisMonth) || {
        month: thisMonth,
        qty: 0,
        tax: 0,
        net: 0,
        unpaidCount: 0,
        paidCount: 0,
        records: [],
      }
    );
  }, [months, thisMonth]);

  const topMonth = useMemo(() => {
    const sorted = [...months].sort((a, b) => b.net - a.net);
    return sorted[0]?.month || null;
  }, [months]);

  // ---------- Month modal ----------
  const openMonth = (m) => {
    setSelectedEventInMonth(null);
    setSelectedZone(null);
    setShowRawEventRecords(false);
    setSelectedMonth(m);
  };
  const closeMonth = () => {
    setSelectedEventInMonth(null);
    setSelectedZone(null);
    setShowRawEventRecords(false);
    setSelectedMonth(null);
  };

  const deleteOwnRecord = async (rec) => {
    if (readOnly || isLockedRecord(rec) || deletingId) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setNotification("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    const ok = window.confirm(
      `ลบรายการ ${rec.eventName || "ค่ากด"} ${rec.zone || rec.priceLabel || ""} จำนวน ${fmt(rec.quantity)} ใบ ใช่ไหม?\n\nเมื่อลบแล้วจะกู้คืนจากหน้าเว็บไม่ได้`
    );
    if (!ok) return;

    try {
      setDeletingId(rec.id);
      // เก็บข้อมูลเดิมไว้ใน Audit Log ก่อนลบ ถ้าเก็บไม่สำเร็จจะไม่ลบ
      await writeAuditLog({ user: currentUser, action: "press-fee.delete", entityType: "press-fee", entityId: rec.id, title: rec.eventName || "ค่ากด", before: rec });
      await deleteDoc(doc(db, `user-press-fee/${currentUser.uid}/records`, rec.id));
      setNotification("ลบรายการแล้ว สามารถกรอกใหม่ได้เลย");
      setTimeout(() => setNotification(""), 3500);
    } catch (error) {
      console.error("delete own record failed", error);
      setNotification("ลบรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setTimeout(() => setNotification(""), 3500);
    } finally {
      setDeletingId("");
    }
  };

  const selectedMonthObj = useMemo(() => {
    if (!selectedMonth) return null;
    return months.find((x) => x.month === selectedMonth) || null;
  }, [months, selectedMonth]);

  // ✅ (1) งานเรียงมากสุด → น้อยสุด (ในเดือนนั้น)
  const monthEventRanking = useMemo(() => {
    if (!selectedMonthObj) return [];

    const map = new Map();

    selectedMonthObj.records.forEach((rec) => {
      const eventName = rec.eventName || "ไม่ระบุชื่องาน";
      const { tax, net } = taxParts(rec);
      const qty = Number(rec.quantity || 0);

      if (!map.has(eventName)) {
        map.set(eventName, {
          eventName,
          qty: 0,
          tax: 0,
          net: 0,
          unpaidCount: 0,
          paidCount: 0,
          records: [],
        });
      }

      const obj = map.get(eventName);
      obj.qty += qty;
      obj.tax += tax;
      obj.net += net;
      obj.records.push(rec);

      if (isSettledRecord(rec)) obj.paidCount += 1;
      else obj.unpaidCount += 1;
    });

    const rows = Array.from(map.values());
    rows.forEach((r) => {
      r.records.sort((a, b) => (b.__date?.getTime?.() || 0) - (a.__date?.getTime?.() || 0));
    });
    rows.sort((a, b) => b.net - a.net);
    return rows;
  }, [selectedMonthObj]);

  // ---------- Event modal ----------
  const openEventInMonth = (eventName) => {
    setSelectedZone(null);
    setShowRawEventRecords(false);
    setSelectedEventInMonth(eventName);
  };
  const closeEventInMonth = () => {
    setSelectedZone(null);
    setShowRawEventRecords(false);
    setSelectedEventInMonth(null);
  };

  const selectedEventObjInMonth = useMemo(() => {
    if (!selectedEventInMonth) return null;
    return monthEventRanking.find((x) => x.eventName === selectedEventInMonth) || null;
  }, [monthEventRanking, selectedEventInMonth]);

  // ✅ (2) สรุปโซน/ราคา แล้วให้กดกรองได้
  const groupedZonesInSelectedEvent = useMemo(() => {
    if (!selectedEventObjInMonth) return [];

    const map = new Map();

    selectedEventObjInMonth.records.forEach((rec) => {
      const zone = rec.zone || rec.priceLabel || "ไม่ระบุโซน";
      const qty = Number(rec.quantity || 0);
      const { tax, net } = taxParts(rec);

      if (!map.has(zone)) {
        map.set(zone, { zone, qty: 0, tax: 0, net: 0, count: 0 });
      }
      const obj = map.get(zone);
      obj.qty += qty;
      obj.tax += tax;
      obj.net += net;
      obj.count += 1;
    });

    const rows = Array.from(map.values());
    rows.sort((a, b) => b.net - a.net);
    return rows;
  }, [selectedEventObjInMonth]);

  const filteredEventRecords = useMemo(() => {
    if (!selectedEventObjInMonth) return [];
    if (!selectedZone) return selectedEventObjInMonth.records;

    return selectedEventObjInMonth.records.filter((rec) => {
      const z = rec.zone || rec.priceLabel || "ไม่ระบุโซน";
      return z === selectedZone;
    });
  }, [selectedEventObjInMonth, selectedZone]);



  return (
    <div className="history-page">
      {notification && <NotificationBar message={notification} onClose={() => setNotification("")} />}

      <div className="history-shell">
        <header className="history-header">
          <div>
            <span className="history-eyebrow">MY PRESS FEE</span>
            <h1>ประวัติค่ากด</h1>
            <p>ดูรายได้ สถานะโอน และรายละเอียดงานย้อนหลังในที่เดียว</p>
          </div>

          <label className="year-picker" aria-label="เลือกปี">
            <span>ปี</span>
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        </header>

        <section className="history-overview">
          <article className="overview-card overview-card--primary">
            <div className="overview-card__label">รายได้สุทธิ ปี {yearFilter}</div>
            <div className="overview-card__value">฿{fmt(yearTotal.net)}</div>
            <div className="overview-card__foot">จาก {fmt(yearTotal.recordCount)} รายการ</div>
          </article>

          <article className="overview-card">
            <div className="overview-icon">🎫</div>
            <div>
              <div className="overview-card__label">จำนวนบัตร</div>
              <div className="overview-card__value overview-card__value--small">{fmt(yearTotal.qty)} <small>ใบ</small></div>
              <div className="overview-card__foot">รวมทุกงานในปีนี้</div>
            </div>
          </article>

          {/* เจ้าของร้าน (ยย) ไม่มีการโอนค่ากด → ซ่อนสถานะการโอน (ยกเว้นมีรายการทีมที่กรอกแทนแล้วยังค้างโอน) */}
          {!(records.some(isOwnerRecord) && yearTotal.unpaidCount === 0) && (
          <article className={`overview-card ${yearTotal.unpaidCount > 0 ? "overview-card--pending" : ""}`}>
            <div className="overview-icon">{yearTotal.unpaidCount > 0 ? "⏳" : "✓"}</div>
            <div>
              <div className="overview-card__label">สถานะการโอน</div>
              <div className="overview-card__value overview-card__value--small">
                {yearTotal.unpaidCount > 0 ? `${fmt(yearTotal.unpaidCount)} รายการ` : "ครบแล้ว"}
              </div>
              <div className="overview-card__foot">
                {yearTotal.unpaidCount > 0 ? `โอนแล้ว ${fmt(yearTotal.paidCount)} รายการ` : "ไม่มีรายการค้างโอน"}
              </div>
            </div>
          </article>
          )}
        </section>

        {Number(yearFilter) === now.getFullYear() && (
          <section className="current-month-panel">
            <div className="current-month-panel__main">
              <div className="current-month-panel__topline">
                <span className="section-kicker">เดือนปัจจุบัน</span>
                {thisMonthObj.records.length === 0 ? (
                  <span className="status-chip status-chip--muted">ยังไม่มีข้อมูล</span>
                ) : thisMonthObj.unpaidCount > 0 ? (
                  <span className="status-chip status-chip--pending">รอโอน {thisMonthObj.unpaidCount}</span>
                ) : (
                  <span className="status-chip status-chip--paid">โอนครบแล้ว</span>
                )}
              </div>
              <h2>{monthNameTH(thisMonth)} {yearFilter}</h2>
              <div className="current-month-panel__amount">฿{fmt(thisMonthObj.net)}</div>
              <div className="current-month-panel__meta">
                <span>{fmt(thisMonthObj.qty)} ใบ</span>
                <span>{fmt(thisMonthObj.records.length)} รายการ</span>
                <span>หัก ณ ที่จ่าย ฿{fmt(thisMonthObj.tax)}</span>
              </div>
            </div>
            <button
              className="secondary-action"
              onClick={() => openMonth(thisMonth)}
              disabled={thisMonthObj.records.length === 0}
            >
              ดูรายละเอียด <span>→</span>
            </button>
          </section>
        )}

        <section className="history-section">
          <div className="section-head">
            <div>
              <span className="section-kicker">YEAR OVERVIEW</span>
              <h2>ภาพรวม 12 เดือน</h2>
              <p>เลือกเดือนเพื่อดูงาน โซน และรายการย่อย</p>
            </div>
          </div>

          <div className="month-grid-modern">
            {months.map((m) => {
              const isTop = m.month === topMonth && m.net > 0;
              const isEmpty = m.records.length === 0;
              const isCurrent = m.month === thisMonth && Number(yearFilter) === now.getFullYear();

              return (
                <button
                  key={m.month}
                  className={`month-tile ${isTop ? "month-tile--top" : ""} ${isEmpty ? "month-tile--empty" : ""} ${isCurrent ? "month-tile--current" : ""}`}
                  onClick={() => openMonth(m.month)}
                  disabled={isEmpty}
                >
                  <div className="month-tile__head">
                    <span className="month-tile__name">{monthNameTH(m.month)}</span>
                    {isCurrent && <span className="tiny-chip">เดือนนี้</span>}
                    {!isCurrent && isTop && <span className="tiny-chip tiny-chip--pink">ยอดสูงสุด</span>}
                  </div>

                  <div className="month-tile__amount">฿{fmt(m.net)}</div>
                  <div className="month-tile__stats">
                    <span>{fmt(m.qty)} ใบ</span>
                    <span>{fmt(m.records.length)} รายการ</span>
                  </div>

                  <div className="month-tile__footer">
                    {isEmpty ? (
                      <span className="status-text status-text--muted">ไม่มีข้อมูล</span>
                    ) : m.unpaidCount > 0 ? (
                      <span className="status-text status-text--pending">● รอโอน {m.unpaidCount}</span>
                    ) : (
                      <span className="status-text status-text--paid">● โอนครบแล้ว</span>
                    )}
                    {!isEmpty && <span className="tile-arrow">→</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {selectedMonthObj && (
        <div className="history-modal" onClick={closeMonth}>
          <div className="history-modal__sheet" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head-modern">
              <div>
                <span className="section-kicker">MONTH DETAIL</span>
                <h3>{monthNameTH(selectedMonthObj.month)} {yearFilter}</h3>
              </div>
              <button className="icon-close" onClick={closeMonth} aria-label="ปิด">×</button>
            </div>

            <div className="modal-summary-grid">
              <div className="modal-summary-item modal-summary-item--primary">
                <span>รายได้สุทธิ</span>
                <strong>฿{fmt(selectedMonthObj.net)}</strong>
              </div>
              <div className="modal-summary-item">
                <span>จำนวนบัตร</span>
                <strong>{fmt(selectedMonthObj.qty)} ใบ</strong>
              </div>
              <div className="modal-summary-item">
                <span>หัก ณ ที่จ่าย</span>
                <strong>฿{fmt(selectedMonthObj.tax)}</strong>
              </div>
            </div>

            <div className="modal-section-head">
              <div>
                <h4>งานในเดือนนี้</h4>
                <p>เรียงตามรายได้สุทธิจากมากไปน้อย</p>
              </div>
              <span>{monthEventRanking.length} งาน</span>
            </div>

            <div className="event-list-modern">
              {monthEventRanking.map((ev, idx) => (
                <button
                  key={ev.eventName}
                  className="event-row-modern"
                  onClick={() => openEventInMonth(ev.eventName)}
                >
                  <div className="event-row-modern__rank">{String(idx + 1).padStart(2, "0")}</div>
                  <div className="event-row-modern__content">
                    <div className="event-row-modern__name">{ev.eventName}</div>
                    <div className="event-row-modern__meta">
                      <span>{fmt(ev.qty)} ใบ</span>
                      <span>{ev.records.length} รายการ</span>
                      {ev.unpaidCount > 0 ? (
                        <span className="inline-status inline-status--pending">รอโอน {ev.unpaidCount}</span>
                      ) : (
                        <span className="inline-status inline-status--paid">โอนแล้ว</span>
                      )}
                    </div>
                  </div>
                  <div className="event-row-modern__amount">฿{fmt(ev.net)}</div>
                  <div className="event-row-modern__arrow">›</div>
                </button>
              ))}

              {monthEventRanking.length === 0 && (
                <div className="empty-state-modern">
                  <div className="empty-state-modern__icon">—</div>
                  <strong>ยังไม่มีรายการในเดือนนี้</strong>
                  <span>เมื่อมีการบันทึกค่ากด รายการจะมาแสดงที่นี่</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedEventObjInMonth && selectedMonthObj && (
        <div className="history-modal history-modal--front" onClick={closeEventInMonth}>
          <div className="history-modal__sheet history-modal__sheet--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head-modern">
              <div className="modal-title-wrap">
                <button className="back-btn" onClick={closeEventInMonth}>←</button>
                <div>
                  <span className="section-kicker">EVENT DETAIL</span>
                  <h3>{selectedEventObjInMonth.eventName}</h3>
                  <p>{monthNameTH(selectedMonthObj.month)} {yearFilter}</p>
                </div>
              </div>
              <button className="icon-close" onClick={closeEventInMonth} aria-label="ปิด">×</button>
            </div>

            <div className="modal-summary-grid">
              <div className="modal-summary-item modal-summary-item--primary">
                <span>รายได้สุทธิ</span>
                <strong>฿{fmt(selectedEventObjInMonth.net)}</strong>
              </div>
              <div className="modal-summary-item">
                <span>จำนวนบัตร</span>
                <strong>{fmt(selectedEventObjInMonth.qty)} ใบ</strong>
              </div>
              <div className="modal-summary-item">
                <span>หัก ณ ที่จ่าย</span>
                <strong>฿{fmt(selectedEventObjInMonth.tax)}</strong>
              </div>
            </div>

            <div className="modal-section-head">
              <div>
                <h4>แยกตามโซน / ราคา</h4>
                <p>เลือกเพื่อดูเฉพาะรายการในโซนนั้น</p>
              </div>
              {selectedZone && (
                <button className="text-action" onClick={() => setSelectedZone(null)}>ล้างตัวกรอง</button>
              )}
            </div>

            <div className="zone-grid-modern">
              {groupedZonesInSelectedEvent.map((z) => {
                const isActive = selectedZone === z.zone;
                return (
                  <button
                    key={z.zone}
                    className={`zone-tile-modern ${isActive ? "zone-tile-modern--active" : ""}`}
                    onClick={() => {
                      setSelectedZone(z.zone);
                      setShowRawEventRecords(true);
                    }}
                  >
                    <div className="zone-tile-modern__head">
                      <span>{z.zone}</span>
                      {isActive && <span className="tiny-chip tiny-chip--pink">กำลังดู</span>}
                    </div>
                    <strong>฿{fmt(z.net)}</strong>
                    <div className="zone-tile-modern__meta">
                      <span>{fmt(z.qty)} ใบ</span>
                      <span>{fmt(z.count)} รายการ</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="records-toolbar">
              <div>
                <h4>รายการย่อย</h4>
                <span>{selectedZone ? `กรอง: ${selectedZone}` : "ทุกโซน"}</span>
              </div>
              <button
                className={`secondary-action secondary-action--compact ${showRawEventRecords ? "is-active" : ""}`}
                onClick={() => setShowRawEventRecords((v) => !v)}
              >
                {showRawEventRecords ? "ซ่อนรายการ" : `ดู ${filteredEventRecords.length} รายการ`}
              </button>
            </div>

            {showRawEventRecords && (
              <div className="record-list-modern">
                {filteredEventRecords.length === 0 ? (
                  <div className="empty-state-modern">
                    <div className="empty-state-modern__icon">—</div>
                    <strong>ไม่พบรายการ</strong>
                    <span>ลองล้างตัวกรองโซนแล้วดูอีกครั้ง</span>
                  </div>
                ) : (
                  filteredEventRecords.map((rec) => {
                    const p = taxParts(rec);
                    return (
                      <article key={rec.id} className="record-card-modern">
                        <div className="record-card-modern__top">
                          <div>
                            <span className="record-zone">{rec.zone || rec.priceLabel || "ไม่ระบุโซน"}</span>
                            <div className="record-date">{formatDateTime(rec.__date)}</div>
                          </div>
                          {isSettledRecord(rec) ? (
                            <span className="status-chip status-chip--paid">{isOwnerRecord(rec) ? "ไม่ต้องโอน" : "โอนแล้ว"}</span>
                          ) : (
                            <span className="status-chip status-chip--pending">รอโอน</span>
                          )}
                        </div>

                        <div className="record-metrics">
                          <div>
                            <span>จำนวน</span>
                            <strong>{fmt(rec.quantity)} ใบ</strong>
                          </div>
                          <div>
                            <span>หัก ณ ที่จ่าย</span>
                            <strong>฿{fmt(p.tax)}</strong>
                          </div>
                          <div className="record-metrics__net">
                            <span>ได้รับสุทธิ</span>
                            <strong>฿{fmt(p.net)}</strong>
                          </div>
                        </div>

                        {!readOnly && (
                          <div className="record-card-modern__actions">
                            {isLockedRecord(rec) ? (
                              <span className="history-locked-note">ล็อกหลังโอนแล้ว</span>
                            ) : (
                              <>
                                {isOwnerRecord(rec) && ownerEditId === rec.id ? (
                                  <div className="history-owner-edit">
                                    <label>
                                      <span>ค่ากดต่อใบ</span>
                                      <input type="number" min="0" step="0.01" value={ownerEditForm.fee} onChange={(e) => setOwnerEditForm((p) => ({ ...p, fee: e.target.value }))} />
                                    </label>
                                    <label>
                                      <span>จำนวนใบ</span>
                                      <input type="number" min="1" step="1" value={ownerEditForm.qty} onChange={(e) => setOwnerEditForm((p) => ({ ...p, qty: e.target.value }))} />
                                    </label>
                                    <strong>รวม ฿{fmt(Number(ownerEditForm.fee || 0) * Number(ownerEditForm.qty || 0))}</strong>
                                    <button type="button" className="history-owner-save" disabled={savingOwnerEdit} onClick={() => saveOwnerEdit(rec)}>
                                      {savingOwnerEdit ? "กำลังบันทึก..." : "บันทึก"}
                                    </button>
                                    <button type="button" className="history-owner-cancel" disabled={savingOwnerEdit} onClick={() => setOwnerEditId("")}>ยกเลิก</button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="history-locked-note">
                                      {isOwnerRecord(rec) ? "กรอกผิดแก้หรือลบได้" : "กรอกผิดสามารถลบแล้วกรอกใหม่ได้"}
                                    </span>
                                    {isOwnerRecord(rec) && (
                                      <button type="button" className="history-owner-edit-btn" onClick={() => openOwnerEdit(rec)}>แก้ไข</button>
                                    )}
                                    <button
                                      type="button"
                                      className="danger-text-btn"
                                      disabled={deletingId === rec.id}
                                      onClick={() => deleteOwnRecord(rec)}
                                    >
                                      {deletingId === rec.id ? "กำลังลบ..." : "ลบรายการ"}
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
